import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CoinsIcon, DeviceMobileIcon, WalletIcon, ReceiptIcon, TrendUpIcon,
  CylinderIcon, TruckIcon, WrenchIcon, ArrowsClockwiseIcon,
} from "@phosphor-icons/react";

const TXN_LABEL = {
  refill: "Refill",
  new_connection: "New Connection",
  tv_in: "TV In",
  surrender: "Surrender",
  tv_out: "TV Out",
  delivery_out: "Delivery Loaded",
  delivery_in: "Refill Confirmed",
  delivery_new_connection: "New Conn. via Boy",
  delivery_return_full: "Return Unsold",
  defective_in: "Defective In",
  defective_out_to_filled: "Def. → Full",
  defective_out_to_empty: "Def. → Empty",
  defective_out_to_full_out: "Def. → Full-Out",
  manual_filled_in: "Manual Filled +",
  manual_filled_out: "Manual Filled −",
  manual_empty_in: "Manual Empty +",
  manual_empty_out: "Manual Empty −",
  plant_in: "Plant · Filled In",
  plant_out_empty: "Plant · Empty Out",
};

export default function Dashboard() {
  const [today] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [stock, setStock] = useState([]);
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [d, s, a] = await Promise.all([
          api.get(`/dashboard/daily?date=${today}`),
          api.get(`/stock`),
          api.get(`/alerts`),
        ]);
        setData(d.data);
        setStock(s.data);
        setAlerts(a.data);
      } catch (e) {
        toast.error("Failed to load dashboard");
      }
    };
    load();
  }, [today]);

  const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex items-end justify-between border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-slate-500">Today · {today}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-2">
            Daily Ledger
          </h1>
          <p className="text-slate-500 mt-2 max-w-lg">
            A single glance at every rupee collected, spent and every cylinder that moved today.
          </p>
        </div>
        <div className="hidden sm:block text-right">
          <div className="overline text-slate-500">Net today</div>
          <div className={`font-mono-num text-3xl font-bold mt-1 ${(data?.totals?.net ?? 0) >= 0 ? "text-slate-900" : "text-[#FF2400]"}`} data-testid="net-total">
            {money(data?.totals?.net)}
          </div>
        </div>
      </div>

      {/* Collection KPIs */}
      <section>
        <div className="overline text-slate-500 mb-3">Collections</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-slate-200 bg-white">
          <Kpi label="Cash" value={money(data?.collections?.cash)} icon={CoinsIcon} testid="kpi-cash" />
          <Kpi label="Google Pay" value={money(data?.collections?.gpay)} icon={DeviceMobileIcon} testid="kpi-gpay" />
          <Kpi label="Wallet" value={money(data?.collections?.wallet)} icon={WalletIcon} testid="kpi-wallet" />
          <Kpi label="Total" value={money(data?.totals?.collection)} icon={TrendUpIcon} accent testid="kpi-total-collection" />
        </div>
      </section>

      {/* Expenses */}
      <section>
        <div className="overline text-slate-500 mb-3">Expenses</div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-0 border border-slate-200 bg-white">
          <Kpi label="Cash Expense" value={money(data?.expenses?.cash)} icon={ReceiptIcon} testid="kpi-exp-cash" />
          <Kpi label="Online Expense" value={money(data?.expenses?.online)} icon={ReceiptIcon} testid="kpi-exp-online" />
          <Kpi label="Total Expense" value={money(data?.totals?.expense)} icon={ReceiptIcon} accent testid="kpi-total-expense" />
        </div>
      </section>

      {/* Stock */}
      <section>
        <div className="overline text-slate-500 mb-3">Cylinder Stock</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {stock.map((t) => (
            <StockCard key={t.id} t={t} />
          ))}
          {stock.length === 0 && (
            <div className="text-sm text-slate-400 border border-dashed border-slate-300 p-6">No cylinder types yet. Add one from the Cylinders page.</div>
          )}
        </div>
      </section>

      {/* Today's Movements — compact 4-tile summary per user request */}
      <section>
        <div className="overline text-slate-500 mb-3">Today's Movements</div>
        <div className="border border-slate-200 bg-white">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {(() => {
              const t = data?.transactions || {};
              const defectiveInStock = stock.reduce((s, x) => s + (x.stock?.defective || 0), 0);
              const tiles = [
                { label: "Refill Cylinder", value: t.refill || 0, testid: "movement-refill" },
                { label: "New Connection / TV In", value: (t.new_connection || 0) + (t.tv_in || 0), testid: "movement-newconn-tvin" },
                { label: "Surrender / TV Out", value: (t.surrender || 0) + (t.tv_out || 0), testid: "movement-surrender-tvout" },
                { label: "Defective in Stock", value: defectiveInStock, testid: "movement-defective-stock" },
              ];
              return tiles.map((tile) => (
                <div key={tile.testid} className="border-r border-b border-slate-200 last:border-r-0 p-6">
                  <div className="overline text-slate-500" style={{ fontSize: 27, letterSpacing: "0.05em" }}>{tile.label}</div>
                  <div
                    className="font-mono-num font-bold mt-3"
                    style={{ fontSize: 54, lineHeight: 1 }}
                    data-testid={tile.testid}
                  >
                    {tile.value}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <div
      className={`p-6 border-r border-b border-slate-200 last:border-r-0 ${accent ? "bg-slate-900 text-white" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className={`overline ${accent ? "text-white/60" : "text-slate-500"}`}>{label}</span>
        <Icon size={16} weight="regular" className={accent ? "text-white/70" : "text-slate-400"} />
      </div>
      <div className="font-mono-num text-2xl sm:text-3xl font-bold mt-3">{value}</div>
    </div>
  );
}

function StockCard({ t }) {
  const isDomestic = t.category === "domestic";
  const accent = isDomestic ? "#FF2400" : "#002FA7";
  return (
    <div className={`border bg-white p-5 relative ${t.low_stock ? "border-[#FF2400]" : "border-slate-200"}`} data-testid={`stock-card-${t.id}`}>
      {t.low_stock && (
        <div className="absolute top-0 right-0 bg-[#FF2400] text-white text-xs uppercase tracking-wider px-2 py-1" data-testid={`low-badge-${t.id}`}>
          Low Stock
        </div>
      )}
      <div className="flex items-start justify-between">
        <div>
          <div className="overline" style={{ color: accent }}>{t.category}</div>
          <div className="font-display font-bold text-lg mt-1">{t.name}</div>
          <div className="text-xs text-slate-500 font-mono-num">{t.size_kg} kg · min {t.min_full || 0}</div>
        </div>
        <div
          className="w-10 h-10 border flex items-center justify-center"
          style={{ borderColor: accent, color: accent }}
          aria-hidden="true"
        >
          <CylinderIcon size={22} weight="regular" />
        </div>
      </div>
      <div className="grid grid-cols-5 gap-2 mt-5">
        <StockNum icon={CylinderIcon} label="Full" val={t.stock.full} alert={t.low_stock} />
        <StockNum icon={ArrowsClockwiseIcon} label="Empty" val={t.stock.empty} />
        <StockNum icon={TruckIcon} label="Boy" val={t.stock.out_with_boy} />
        <StockNum icon={CylinderIcon} label="Out" val={t.stock.full_out || 0} accent />
        <StockNum icon={WrenchIcon} label="Def." val={t.stock.defective} />
      </div>
    </div>
  );
}

function StockNum({ icon: Icon, label, val, alert, accent }) {
  let colorClass = "";
  if (alert) colorClass = "text-[#FF2400]";
  else if (accent) colorClass = "text-[#002FA7]";
  return (
    <div className="border border-slate-100 p-2">
      <div className="flex items-center gap-1 text-slate-400"><Icon size={14} /><span className="overline" style={{ fontSize: 12 }}>{label}</span></div>
      <div className={`font-mono-num font-bold text-lg mt-1 ${colorClass}`}>{val}</div>
    </div>
  );
}

function AlertCard({ label, count, items, testid }) {
  return (
    <div className="border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-600">{label}</span>
        <span className={`font-mono-num font-bold text-lg ${count > 0 ? "text-[#FF2400]" : "text-slate-300"}`}>{count}</span>
      </div>
      {items.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-slate-600">
          {items.slice(0, 3).map((s) => <li key={s} className="truncate">· {s}</li>)}
          {items.length > 3 && <li className="text-slate-400">+{items.length - 3} more</li>}
        </ul>
      )}
    </div>
  );
}
